import { FC, useState } from 'react'
import dayjs from 'dayjs'
import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import { CampaignOutlined, MailOutline, SignpostOutlined } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import { useBoardsList, useBoardsListPosts } from 'services/boards/boards.hooks'
import { useNotificationsList } from 'services/notifications/notifications.hook'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import NoActiveContractMessage from 'components/NoActiveContractMessage'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import WallCalendar from './WallCalendar'

type WallView = 'board' | 'messages' | 'calendar'
type BoardFilter = 'all' | 'new'

const VIEWS: { value: WallView; i18n: { id: string; defaultMessage: string } }[] = [
  { value: 'board', i18n: { id: 'wallList.board', defaultMessage: 'Tablica' } },
  { value: 'messages', i18n: { id: 'boardsMessagesPage.label', defaultMessage: 'Wiadomości' } },
  { value: 'calendar', i18n: { id: 'wallList.calendar', defaultMessage: 'Kalendarz' } }
]

// Posts newer than this are marked with the "unread" dot and match the "Nowe" filter
// (same rule as the dashboard announcements widget).
const NEW_POST_DAYS = 7

// Fallback gradients for posts without a cover image, rotated by card index.
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #7F71D3 0%, #6A8AE5 100%)',
  'linear-gradient(135deg, #1F1F73 0%, #6A8AE5 100%)',
  'linear-gradient(135deg, #6A8AE5 0%, #24D4E1 100%)',
  'linear-gradient(135deg, #1F1F73 0%, #24D4E1 100%)',
  'linear-gradient(135deg, #7F71D3 0%, #24D4E1 100%)'
]

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

// Komunikaty list ("Panel Klienta 3.0"): board posts / messages / calendar views.
const WallList: FC = () => {
  const [view, setView] = useState<WallView>('board')
  const [boardFilter, setBoardFilter] = useState<BoardFilter>('all')
  const { data = [] } = useBoardsList()
  const { boardsListPosts, isLoading } = useBoardsListPosts(data.map(({ id }) => id))
  const { messagesList } = useNotificationsList()
  const { formatMessage } = useIntl()
  const { leaseContractsList, isLoading: isLeaseLoading } = useLeaseContractsList()

  const activeLeases = leaseContractsList?.filter(({ leaseStatus }) => leaseStatus === 0) || []

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  if (isLeaseLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (activeLeases.length === 0) {
    return <NoActiveContractMessage />
  }

  const isNewPost = (eventDate: string) =>
    dayjs(eventDate).isAfter(dayjs().subtract(NEW_POST_DAYS, 'day'))

  const badgeSx = {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    padding: '3px 10px',
    backgroundColor: alpha(theme.r4r.azure, 0.15),
    color: theme.r4r.azure,
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 'normal',
    whiteSpace: 'nowrap'
  }

  const renderBoardFilters = () => {
    const filters: { value: BoardFilter; label: string }[] = [
      {
        value: 'all',
        label: formatMessage({ id: 'wallList.filterAll', defaultMessage: 'Wszystkie' })
      },
      { value: 'new', label: formatMessage({ id: 'wallList.filterNew', defaultMessage: 'Nowe' }) }
    ]

    return (
      <Box sx={{ display: 'flex', gap: '8px', mb: '16px' }}>
        {filters.map(({ value, label }) => {
          const isActive = boardFilter === value
          return (
            <ButtonBase
              key={value}
              onClick={() => setBoardFilter(value)}
              sx={{
                padding: '7px 15px',
                fontFamily: theme.typography.fontFamily,
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: '12px',
                whiteSpace: 'nowrap',
                border: `1px solid ${isActive ? theme.r4r.navy : theme.r4r.greyLight}`,
                backgroundColor: isActive ? alpha(theme.r4r.navy, 0.08) : '#fff',
                color: isActive ? theme.r4r.navy : theme.r4r.grey
              }}>
              {label}
            </ButtonBase>
          )
        })}
      </Box>
    )
  }

  const renderBoard = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )
    }

    const posts = [...boardsListPosts]
      .sort((a, b) => dayjs(b.eventDate).valueOf() - dayjs(a.eventDate).valueOf())
      .filter((post) => boardFilter === 'all' || isNewPost(post.eventDate))

    if (!posts.length) {
      return (
        <>
          {renderBoardFilters()}
          <HelpdeskEmptyState
            Icon={SignpostOutlined}
            title={formatMessage({ id: 'wallList.emptyCalendar', defaultMessage: 'Brak ogłoszeń.' })}
          />
        </>
      )
    }

    return (
      <>
        {renderBoardFilters()}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'repeat(3, minmax(0, 1fr))' : '1fr',
            gap: '18px',
            pt: '4px'
          }}>
          {posts.map((post, index) => {
            const cover = post.multimedia[0]?.multimedia
            const description = stripHtml(post.text)
            return (
              <ButtonBase
                key={`${post.board.id}/${post.id}`}
                onClick={() => navigate(`/board/${post.board.id}/${post.id}`)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
                  ':hover': { boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }
                }}>
                <Box
                  sx={{
                    height: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    background: cover ? undefined : CARD_GRADIENTS[index % CARD_GRADIENTS.length]
                  }}>
                  {cover ? (
                    <Box
                      component='img'
                      src={cover}
                      alt=''
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <CampaignOutlined sx={{ fontSize: '32px', color: alpha('#fff', 0.6) }} />
                  )}
                </Box>
                <Box
                  sx={{
                    padding: '16px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    width: '100%'
                  }}>
                  <Box sx={{ ...badgeSx, mb: '8px' }}>{post.board.name}</Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      lineHeight: '19.6px',
                      color: theme.r4r.ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: '5px'
                    }}>
                    {post.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      lineHeight: '18px',
                      color: theme.r4r.grey,
                      height: '36px',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                    {description}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      mt: '12px',
                      pt: '11px',
                      borderTop: `1px solid ${theme.r4r.greyLight}`
                    }}>
                    <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
                      {post.createdBy.firstName || ''}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: theme.r4r.grey }}>
                      {dayjs(post.eventDate).format('DD.MM.YYYY')}
                    </Typography>
                  </Box>
                </Box>
                {isNewPost(post.eventDate) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      width: '10px',
                      height: '10px',
                      borderRadius: '5px',
                      backgroundColor: theme.r4r.azure
                    }}
                  />
                )}
              </ButtonBase>
            )
          })}
        </Box>
      </>
    )
  }

  const renderMessages = () => {
    if (!messagesList.length && !isLoading) {
      return (
        <HelpdeskEmptyState
          Icon={MailOutline}
          title={formatMessage({ id: 'wallList.emptyMessages', defaultMessage: 'Brak wiadomości.' })}
        />
      )
    }

    return (
      <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow }}>
        {messagesList.map((message, index) => {
          const isSelected = pathname === `/board/messages/${message.id}`
          return (
            <ButtonBase
              key={`${message.id}`}
              onClick={() => navigate(`/board/messages/${message.id}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                width: '100%',
                textAlign: 'left',
                padding: '14px 16px',
                borderBottom:
                  index === messagesList.length - 1 ? 'none' : `1px solid ${theme.r4r.greyLight}`,
                backgroundColor: isSelected && isDesktop ? '#F0F1FF' : 'transparent',
                ':hover': { backgroundColor: isSelected && isDesktop ? '#F0F1FF' : '#F9FAFB' }
              }}>
              <Box
                sx={{
                  width: '44px',
                  height: '44px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(theme.r4r.violet, 0.14),
                  color: theme.r4r.violet
                }}>
                <MailOutline sx={{ fontSize: '20px' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.r4r.ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                  {message.subject}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
                  {dayjs(message.createDate).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Box>
              <Typography sx={{ color: theme.r4r.greyLight, fontSize: '16px' }}>›</Typography>
            </ButtonBase>
          )
        })}
      </Box>
    )
  }

  return (
    <Box pb={isDesktop ? 0 : '90px'}>
      <Box sx={{ display: 'flex', mb: '16px', maxWidth: isDesktop ? '460px' : '100%' }}>
        {VIEWS.map(({ value, i18n }) => {
          const isActive = view === value
          return (
            <ButtonBase
              key={value}
              onClick={() => setView(value)}
              sx={{
                flex: 1,
                width: isDesktop ? '154px' : 'auto',
                padding: '11px 20px',
                fontFamily: theme.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                border: `1px solid ${isActive ? theme.r4r.navy : theme.r4r.greyLight}`,
                backgroundColor: isActive ? theme.r4r.navy : '#fff',
                color: isActive ? '#fff' : theme.r4r.grey,
                marginLeft: '-1px',
                ':first-of-type': { marginLeft: 0 }
              }}>
              {formatMessage(i18n)}
            </ButtonBase>
          )
        })}
      </Box>
      {view === 'calendar' ? <WallCalendar /> : view === 'messages' ? renderMessages() : renderBoard()}
    </Box>
  )
}

export default WallList
